import { computed, Injectable, signal } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { IDestinationsProvider } from "app/js/src/domain/clipping-studio/publish/i-destinations-provider";
import {
  DestinationConfig,
  DestinationMetadataOverride,
  PublishMetadata,
  DestinationType,
  SocialAccount,
  createUnavailableSocialAccount,
  UNAVAILABLE_DESTINATION_DISPLAY_NAME,
} from "app/js/src/domain/clipping-studio/publish/publish-models";
import { AspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";
import { ClippingStudioEditorMode } from "app/js/src/domain/clipping-studio/clipping-studio-editor-mode";
import { ClippingStudioManagerService } from "./clipping-studio-manager.service";
import { IGenerateMetadataRepository } from "app/js/src/domain/clipping-studio/publish/i-generate-metadata-repository";
import { IDestinationTypeDefaultsProvider } from "app/js/src/domain/clipping-studio/publish/i-destination-type-defaults-provider";
import {
  ClipPreviewAspectRatio,
  ClipPreviewEditorState,
  ClipPreviewModel,
  createUploadedImageClipPreviewModel,
  createVideoSnapshotClipPreviewModel,
  fetchHttpImageAsDataUrl,
  getClipPreviewDisplayUrl,
  getClipPreviewEditorSourceFromModel,
  hasClipPreviewModel,
  parseClipPreviewValue,
  reconcileClipPreviewForAspectRatio,
  serializeClipPreviewModel,
  toClipPreviewAspectRatio,
  updateClipPreviewAfterEditorApply,
} from "app/js/src/domain/clipping-studio/clip-preview-model";
import {
  encodeClipPreviewPersistedDataUrl,
  resolveVideoSnapshotEditorSource,
} from "app/js/src/domain/clipping-studio/clip-preview-image-encode";
import { MediaRepository } from "app/js/src/domain/media-repository";
import { UserService } from "app/js/src/domain/user-service";
import {
  PublishModelV1,
  parsePublishModelV1,
  serializePublishModelV1,
  createDefaultDestinationStateV1,
} from "app/js/src/domain/clipping-studio/publish/publish-model-v1";
import type {
  DestinationDetails,
  DestinationPlatform,
  DestinationPlatformData,
  FacebookDestinationPlatformData,
  InstagramDestinationPlatformData,
  TikTokCreatorInfo,
  TikTokDestinationPlatformData,
  YouTubeDestinationPlatformData,
  YouTubeVideoCategoryCatalog,
} from "app/js/src/domain/destinations";
import { TITLE_MAX_LENGTH } from "app/js/src/domain/clipping-studio/publish/publish-models";
import {
  INSTAGRAM_CAPTION_MAX_LENGTH,
  buildInstagramCaptionPreview,
  resolveInstagramPublicationSettings,
} from "app/js/src/domain/destinations/instagram-publication-settings";
import { resolveFacebookPublicationSettings } from "app/js/src/domain/destinations/facebook-publication-settings";
import { resolveYouTubePublicationSettings } from "app/js/src/domain/destinations/youtube-publication-settings";
import type { PublicationSettingsSummaryInput } from "app/js/src/domain/clipping-studio/publish/publish-settings-summary";

export interface PublishBlocker {
  code: string;
  message: string;
}

export interface TikTokPublishContext {
  destinationUid: string;
  destinationName: string;
  maxVideoPostDurationSec: number;
  isBrandContent: boolean;
}

export type DestinationSettingsPanelState = "idle" | "loading" | "ready" | "error";

export interface DestinationSettingsUiState {
  panelState: DestinationSettingsPanelState;
  platform: DestinationPlatform | null;
  details: DestinationDetails | null;
  videoCategories: YouTubeVideoCategoryCatalog | null;
  creatorInfo: TikTokCreatorInfo | null;
  errorMessage: string | null;
  draftPlatformData: DestinationPlatformData | null;
  isSaving: boolean;
}

function createEmptyDestinationConfig(): DestinationConfig {
  return {
    aspectRatio: AspectRatio.Aspect16x9,
  };
}

function createInitialMetadata(): PublishMetadata {
  return {
    title: "",
    message: "",
    tags: "",
  };
}

function parsePublishMetadataJson(raw: string | null | undefined): PublishMetadata {
  const trimmed = raw?.trim();
  if (!trimmed) return createInitialMetadata();
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    return {
      title: typeof parsed.title === "string" ? parsed.title : "",
      message: typeof parsed.message === "string" ? parsed.message : "",
      tags: typeof parsed.tags === "string" ? parsed.tags : "",
    };
  } catch {
    return createInitialMetadata();
  }
}

function formatDurationSeconds(totalSeconds: number): string {
  const safe = Math.max(0, Math.ceil(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function createIdleDestinationSettingsState(): DestinationSettingsUiState {
  return {
    panelState: "idle",
    platform: null,
    details: null,
    videoCategories: null,
    creatorInfo: null,
    errorMessage: null,
    draftPlatformData: null,
    isSaving: false,
  };
}

function cloneDestinationPlatformData(
  data: DestinationPlatformData | null,
): DestinationPlatformData | null {
  if (data == null) return null;
  return { ...data };
}

@Injectable()
export class PublishStateService {
  private selectedIdsSubject = new BehaviorSubject<string[]>([]);
  private selectedIdsSignal = signal<string[]>([]);
  private configsSubject = new BehaviorSubject<
    Record<string, DestinationConfig>
  >({});
  private expandedIdSubject = new BehaviorSubject<string | null>(null);
  private metadataSubject = new BehaviorSubject<PublishMetadata>(
    createInitialMetadata(),
  );
  private destinationMetadataOverridesSubject = new BehaviorSubject<
    Record<string, DestinationMetadataOverride>
  >({});
  private publishModelSubject = new BehaviorSubject<PublishModelV1>(
    parsePublishModelV1({ raw: null, timeZone: "UTC" }),
  );

  private updatePublishModel(
    fn: (current: PublishModelV1) => PublishModelV1,
  ): void {
    const next = fn(this.publishModelSubject.value);
    this.publishModelSubject.next(next);
  }
  private isAICompletingSubject = new BehaviorSubject<boolean>(false);
  private readonly isAICompletingSignal = signal(false);
  /** Destination id selected in the top-bar dropdown; drives preview aspect ratio. null = default 16:9. */
  private selectedPreviewDestinationIdSubject = new BehaviorSubject<
    string | null
  >(null);

  /** Default publish config per destination type (from API or fallback). null until first load. */
  readonly defaultConfigsByType = signal<Record<DestinationType, DestinationConfig> | null>(null);
  /** True while destination-type-defaults are being loaded from the API. */
  readonly defaultConfigsLoading = signal(false);
  /** Error message when loading destination-type-defaults failed, or null. */
  readonly defaultConfigsError = signal<string | null>(null);
  private readonly publishBlockersBySource = signal<Record<string, PublishBlocker[]>>({});
  private readonly tikTokPublishContexts = signal<Record<string, TikTokPublishContext>>({});
  private readonly destinationSettingsUiStateByUid =
    signal<Record<string, DestinationSettingsUiState>>({});
  /** Bumps when destination settings UI cache changes (load / draft / clear) for preview sync. */
  private readonly destinationSettingsUiRevisionSubject = new BehaviorSubject<number>(0);
  readonly destinationSettingsUiRevision$ =
    this.destinationSettingsUiRevisionSubject.asObservable();
  readonly preSubmitError = signal<string | null>(null);
  readonly publishBlockers = computed(() =>
    Object.values(this.publishBlockersBySource()).flat(),
  );
  readonly canPublish = computed(
    () => this.selectedIdsSignal().length > 0 && this.publishBlockers().length === 0,
  );
  readonly publishDisabledReason = computed(() => {
    if (this.selectedIdsSignal().length === 0) {
      return 'Select at least one destination.';
    }
    return this.publishBlockers()[0]?.message ?? null;
  });

  /**
   * Destination-settings blockers only (TikTok privacy/disclosure/creator info,
   * Facebook Reel eligibility). Excludes metadata title/caption blockers so the
   * Dest wizard step can advance before Meta is filled.
   */
  readonly destinationSettingsBlockers = computed(() => {
    const bySource = this.publishBlockersBySource();
    return Object.entries(bySource)
      .filter(
        ([source]) =>
          source.startsWith("tiktok:") || source.startsWith("facebook:"),
      )
      .flatMap(([, blockers]) => blockers);
  });

  readonly canLeaveDestStep = computed(
    () =>
      this.selectedIdsSignal().length > 0 &&
      this.destinationSettingsBlockers().length === 0,
  );

  readonly destStepDisabledReason = computed(() => {
    if (this.selectedIdsSignal().length === 0) {
      return "Select at least one destination.";
    }
    return this.destinationSettingsBlockers()[0]?.message ?? null;
  });

  /**
   * Metadata blockers for the Meta wizard step (title + Instagram caption limits).
   * Excludes destination-settings blockers (tiktok/facebook).
   */
  readonly metadataStepBlockers = computed(() => {
    const bySource = this.publishBlockersBySource();
    return Object.entries(bySource)
      .filter(
        ([source]) =>
          source.startsWith("title:") ||
          source.startsWith("instagram-caption:"),
      )
      .flatMap(([, blockers]) => blockers);
  });

  readonly canLeaveMetaStep = computed(
    () =>
      !this.isAICompletingSignal() && this.metadataStepBlockers().length === 0,
  );

  readonly metaStepDisabledReason = computed(() => {
    return this.metadataStepBlockers()[0]?.message ?? null;
  });

  readonly selectedTikTokPublishContexts = computed(() => {
    const selected = new Set(this.selectedIdsSignal());
    return Object.values(this.tikTokPublishContexts())
      .filter((ctx) => selected.has(ctx.destinationUid));
  });
  readonly tikTokDeclaration = computed(() => {
    const contexts = this.selectedTikTokPublishContexts();
    if (contexts.length === 0) {
      return null;
    }
    return contexts.some((ctx) => ctx.isBrandContent)
      ? "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation."
      : "By posting, you agree to TikTok's Music Usage Confirmation.";
  });

  readonly selectedDestinations$ = this.selectedIdsSubject.asObservable();
  readonly destinationMetadataOverrides$ =
    this.destinationMetadataOverridesSubject.asObservable();
  readonly configs$ = this.configsSubject.asObservable();
  readonly selectedPreviewDestinationId$ =
    this.selectedPreviewDestinationIdSubject.asObservable();
  readonly expandedDestinationId$ = this.expandedIdSubject.asObservable();
  readonly publishModel$ = this.publishModelSubject.asObservable();
  readonly projectClipPreview$ = this.manager.state$.pipe(
    map((s) => s.clipPreview),
    distinctUntilChanged(),
  );
  readonly metadata$ = this.metadataSubject.asObservable();
  readonly isAICompleting$ = this.isAICompletingSubject.asObservable();
  /** True when current media has a video slug (required for AI generate-metadata). */
  readonly canRunAiMetadata$ = this.manager.state$.pipe(
    map((s) => !!s.media?.videoSlug?.trim()),
  );
  // EPG integration removed from clipping studio.

  /** Data URL / image src from clip_preview (global or per-destination override). */
  readonly clipPreviewDisplayUrl$: Observable<string | null>;

  readonly destinations$: Observable<SocialAccount[]>;

  private persistTrigger$ = new Subject<void>();
  /** Pre-selected destination uids pending settings bootstrap after project hydrate. */
  private preselectedUidsPendingBootstrap: string[] | null = null;
  private readonly projectPublishHydratedSubject = new Subject<void>();
  /** Fires once after hydrate when the opened project already has checked destinations. */
  readonly projectPublishHydrated$ =
    this.projectPublishHydratedSubject.asObservable();

  get currentEditorMode(): ClippingStudioEditorMode {
    return this.manager.state.mode;
  }

  constructor(
    private destinationsProvider: IDestinationsProvider,
    private manager: ClippingStudioManagerService,
    private generateMetadataRepository: IGenerateMetadataRepository,
    private destinationTypeDefaultsProvider: IDestinationTypeDefaultsProvider,
    private userService: UserService,
    private mediaRepository: MediaRepository,
  ) {
    const destinations = this.destinationsProvider.getDestinations();
    this.loadDestinationTypeDefaults();
    this.configsSubject.next(this.buildConfigsFromDestinations(destinations));
    this.clipPreviewDisplayUrl$ = this.manager.state$.pipe(
      map((s) => getClipPreviewDisplayUrl(s.clipPreview)),
    );
    this.manager.state$
      .pipe(
        map((s) => s.projectId ?? null),
        distinctUntilChanged(),
      )
      .subscribe((projectId) => {
        if (projectId == null || projectId <= 0) {
          this.applyDefaultPublishState();
        }
        // Publish fields are hydrated from GET .../open via hydrateFromProjectPublishFields.
      });
    this.persistTrigger$
      .pipe(debounceTime(1500))
      .subscribe(() => this.flushDebouncedProjectSave());
    this.destinations$ = new BehaviorSubject(destinations).asObservable();
  }

  private getDefaultConfigForType(type: DestinationType): DestinationConfig {
    const fromApi = this.defaultConfigsByType();
    const config = fromApi?.[type] ?? createEmptyDestinationConfig();
    return { ...config };
  }

  private buildConfigsFromDestinations(
    destinations: { id: string; type: DestinationType }[],
  ): Record<string, DestinationConfig> {
    const configs: Record<string, DestinationConfig> = {};
    for (const d of destinations) {
      configs[d.id] = this.getDefaultConfigForType(d.type);
    }
    return configs;
  }

  private loadDestinationTypeDefaults(): void {
    this.defaultConfigsLoading.set(true);
    this.defaultConfigsError.set(null);
    this.destinationTypeDefaultsProvider
      .getDefaultConfigs()
      .then((configs) => {
        this.defaultConfigsByType.set(configs);
      })
      .catch((err) => {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to load destination type defaults.";
        this.defaultConfigsError.set(message);
      })
      .finally(() => {
        this.defaultConfigsLoading.set(false);
      });
  }

  private applyDefaultPublishState(): void {
    this.metadataSubject.next(createInitialMetadata());
    this.destinationMetadataOverridesSubject.next({});
    this.setSelectedIdsInternal([]);
    this.publishBlockersBySource.set({});
    this.tikTokPublishContexts.set({});
    this.destinationSettingsUiStateByUid.set({});
    this.bumpDestinationSettingsUiRevision();
    this.setPreSubmitError(null);
    this.preselectedUidsPendingBootstrap = null;
    const destinations = this.destinationsProvider.getDestinations();
    this.configsSubject.next(this.buildConfigsFromDestinations(destinations));
    this.selectedPreviewDestinationIdSubject.next(null);
  }

  /**
   * Hydrates publish tab metadata from project row fields returned by GET .../publications/{uid}/open.
   */
  hydrateFromProjectPublishFields(
    publishMetadataJson?: string | null,
    publishMetadataOverridesJson?: string | null,
  ): void {
    const tz = this.userService.getDisplayTimezone();
    const model = parsePublishModelV1({
      raw: publishMetadataOverridesJson ?? null,
      timeZone: tz,
    });
    this.publishModelSubject.next(model);

    this.metadataSubject.next(parsePublishMetadataJson(publishMetadataJson));

    const overrides: Record<string, DestinationMetadataOverride> = {};
    const configs: Record<string, DestinationConfig> = {};
    const selectedIds: string[] = [];

    for (const [uid, d] of Object.entries(model.destinations)) {
      selectedIds.push(uid);
      overrides[uid] = {
        title: d.metadata.title,
        tags: d.metadata.tags,
        message: d.metadata.message,
      };
      configs[uid] = { aspectRatio: d.publish.aspectRatio };
    }

    this.setSelectedIdsInternal(selectedIds);
    this.destinationMetadataOverridesSubject.next(overrides);
    if (Object.keys(configs).length > 0) {
      const destinations = this.destinationsProvider.getDestinations();
      const merged = this.buildConfigsFromDestinations(destinations);
      Object.assign(merged, configs);
      this.configsSubject.next(merged);
    }

    if (selectedIds.length > 0) {
      this.preselectedUidsPendingBootstrap = [...selectedIds];
      this.projectPublishHydratedSubject.next();
    } else {
      this.preselectedUidsPendingBootstrap = null;
    }

    this.syncTitleBlockers();
    this.syncInstagramCaptionBlockers();
  }

  /**
   * Consumes destination uids that were checked when the project was hydrated from the API.
   * Returns null when there is nothing to bootstrap (one-shot).
   */
  consumePreselectedUidsPendingBootstrap(): string[] | null {
    if (!this.preselectedUidsPendingBootstrap?.length) {
      return null;
    }
    const uids = [...this.preselectedUidsPendingBootstrap];
    this.preselectedUidsPendingBootstrap = null;
    return uids;
  }

  getDestinationSettingsOverride(
    destinationUid: string,
  ): DestinationPlatformData | null {
    return (
      this.publishModelSubject.value.destinations[destinationUid]?.publish
        .destinationSettings ?? null
    );
  }

  /** Uids persisted in publish_metadata_overrides_json — checked destinations only. */
  private collectDestinationUidsForPublishSave(): string[] {
    return [...this.selectedIdsSubject.value];
  }

  /** Serialized publish columns for PUT .../projects/{projectId}. */
  getPublishMetadataForProjectSave(): {
    publishMetadataJson: string;
    publishMetadataOverridesJson: string;
  } {
    const tz = this.userService.getDisplayTimezone();
    const inMemory = this.publishModelSubject.value;
    const metaOverrides = this.destinationMetadataOverridesSubject.value;
    const destinations: PublishModelV1["destinations"] = {};

    for (const uid of this.collectDestinationUidsForPublishSave()) {
      const fromModel = inMemory.destinations[uid];
      const metaOverride = metaOverrides[uid] ?? fromModel?.metadata ?? {};
      const config = this.getConfig(uid);
      destinations[uid] = createDefaultDestinationStateV1({
        timeZone: tz,
        metadata: metaOverride,
        aspectRatio: config.aspectRatio,
        destinationSettings: fromModel?.publish.destinationSettings ?? null,
        clipPreview: fromModel?.clipPreview ?? null,
      });
    }

    const model: PublishModelV1 = {
      version: 1,
      destinations,
    };

    this.updatePublishModel((m) => ({
      ...m,
      destinations: { ...m.destinations, ...destinations },
    }));

    return {
      publishMetadataJson: JSON.stringify(this.metadataSubject.value),
      publishMetadataOverridesJson: serializePublishModelV1(model),
    };
  }

  getThumbnailDisplayUrl(destinationUid: string | null): string | null {
    if (!destinationUid) {
      return getClipPreviewDisplayUrl(this.manager.state.clipPreview);
    }
    const override = this.publishModelSubject.value.destinations[destinationUid]?.clipPreview;
    if (override) {
      return getClipPreviewDisplayUrl(serializeClipPreviewModel(override));
    }
    return getClipPreviewDisplayUrl(this.manager.state.clipPreview);
  }

  /**
   * Cover image for a future publication: per-destination clipPreview only
   * (no fallback to project-global clip_preview). Matches platform submit resolution.
   */
  getPublicationCoverDisplayUrl(destinationUid: string): string | null {
    if (!destinationUid?.trim()) {
      return null;
    }
    const override =
      this.publishModelSubject.value.destinations[destinationUid]?.clipPreview ?? null;
    if (!override) {
      return null;
    }
    return getClipPreviewDisplayUrl(serializeClipPreviewModel(override));
  }

  async resolveClipPreviewForEditor(destinationUid: string | null): Promise<{
    model: ClipPreviewModel | null;
    editorActiveRatio: ClipPreviewAspectRatio;
    editor: ClipPreviewEditorState;
    editorImageUrl: string | null;
    uploadedImage: string | null;
  }> {
    const targetRatio = destinationUid
      ? toClipPreviewAspectRatio(this.getConfig(destinationUid).aspectRatio)
      : ("16:9" as ClipPreviewAspectRatio);

    let baseModel: ClipPreviewModel | null = null;
    if (destinationUid) {
      const override =
        this.publishModelSubject.value.destinations[destinationUid]?.clipPreview ?? null;
      if (override) {
        baseModel = override;
      } else {
        baseModel = parseClipPreviewValue(this.manager.state.clipPreview);
      }
    } else {
      baseModel = parseClipPreviewValue(this.manager.state.clipPreview);
    }

    const reconciled = reconcileClipPreviewForAspectRatio(baseModel, targetRatio);
    const model = reconciled.model;
    let editorImageUrl: string | null = null;
    const uploadedImage =
      model?.sourceType === "uploaded-image" ? model.uploadedImage : null;

    if (model?.sourceType === "video-snapshot") {
      editorImageUrl = await resolveVideoSnapshotEditorSource({
        captureFrame: () =>
          this.manager.captureClipPreviewFrameAt(model.source.timeMs, {
            overlayTimeMs: model.source.fullEditMs ?? null,
            destinationUid,
          }),
        fallbackPreviewBase64: model.previewBase64 ?? null,
      });
    } else if (model?.sourceType === "uploaded-image") {
      editorImageUrl = model.uploadedImage;
    } else {
      editorImageUrl = getClipPreviewEditorSourceFromModel(model);
    }

    if (!editorImageUrl) {
      editorImageUrl = this.getThumbnailDisplayUrl(destinationUid);
    }

    return {
      model,
      editorActiveRatio: reconciled.editorActiveRatio,
      editor: reconciled.editor,
      editorImageUrl,
      uploadedImage,
    };
  }

  async applyClipPreviewFromEditor(
    destinationUid: string | null,
    args: {
      previewBase64: string;
      aspectRatio: ClipPreviewAspectRatio;
      editor: ClipPreviewEditorState;
      uploadedImage?: string | null;
    },
  ): Promise<void> {
    const persisted = await this.encodePersistedClipPreviewApplyArgs(args);

    if (!destinationUid) {
      this.manager.setClipPreviewFromEditorResult(persisted);
      return;
    }

    const tz = this.userService.getDisplayTimezone();
    const existing =
      this.publishModelSubject.value.destinations[destinationUid]?.clipPreview ?? null;
    const currentSerialized = existing
      ? serializeClipPreviewModel(existing)
      : this.manager.state.clipPreview;

    const model = updateClipPreviewAfterEditorApply({
      current: currentSerialized,
      previewBase64: persisted.previewBase64,
      aspectRatio: persisted.aspectRatio,
      editor: persisted.editor,
      uploadedImage: persisted.uploadedImage,
    });

    const config = this.getConfig(destinationUid);
    this.updatePublishModel((m) => {
      const prev = m.destinations[destinationUid];
      const dest =
        prev ??
        createDefaultDestinationStateV1({
          timeZone: tz,
          aspectRatio: config.aspectRatio,
        });
      return {
        ...m,
        destinations: {
          ...m.destinations,
          [destinationUid]: {
            ...dest,
            clipPreview: model,
            publish: {
              ...dest.publish,
              aspectRatio: config.aspectRatio,
            },
          },
        },
      };
    });

    void this.manager.saveProject();
  }

  async applyClipPreviewSnapshot(args: {
    previewBase64: string;
    aspectRatio: ClipPreviewAspectRatio;
    timeMs?: number;
    fullEditMs?: number | null;
    destinationUid?: string | null;
  }): Promise<void> {
    const persistedPreview =
      (await encodeClipPreviewPersistedDataUrl(args.previewBase64)) ?? args.previewBase64;

    if (!args.destinationUid) {
      this.manager.setClipPreviewSnapshot({
        previewBase64: persistedPreview,
        aspectRatio: args.aspectRatio,
        timeMs: args.timeMs,
        fullEditMs: args.fullEditMs,
      });
      return;
    }

    const tz = this.userService.getDisplayTimezone();
    const model = createVideoSnapshotClipPreviewModel({
      previewBase64: persistedPreview,
      mediaId: this.manager.state.media?.id ?? null,
      timeMs: args.timeMs ?? this.manager.getCurrentSourceTimeMs(),
      fullEditMs: args.fullEditMs ?? null,
      aspectRatio: args.aspectRatio,
    });
    const config = this.getConfig(args.destinationUid);
    this.updatePublishModel((m) => {
      const prev = m.destinations[args.destinationUid!];
      const dest =
        prev ??
        createDefaultDestinationStateV1({
          timeZone: tz,
          aspectRatio: config.aspectRatio,
        });
      return {
        ...m,
        destinations: {
          ...m.destinations,
          [args.destinationUid!]: {
            ...dest,
            clipPreview: model,
            publish: {
              ...dest.publish,
              aspectRatio: config.aspectRatio,
            },
          },
        },
      };
    });
    void this.manager.saveProject();
  }

  /**
   * When global clip_preview is empty, load MAM contentSnapshotUrl via getMediaInfo,
   * convert to base64, and persist as uploaded-image ClipPreviewModel.
   */
  async bootstrapGlobalClipPreviewFromMamSnapshot(): Promise<void> {
    if (hasClipPreviewModel(this.manager.state.clipPreview)) {
      return;
    }
    const mediaId = this.manager.state.media?.id;
    if (mediaId == null || mediaId <= 0) {
      return;
    }

    try {
      const info = await this.mediaRepository.getMediaInfo(mediaId);
      const snapshotUrl = info?.contentSnapshotUrl?.trim();
      if (!snapshotUrl) {
        return;
      }

      const dataUrl = await fetchHttpImageAsDataUrl(snapshotUrl);
      if (!dataUrl) {
        return;
      }

      const persisted =
        (await encodeClipPreviewPersistedDataUrl(dataUrl)) ?? dataUrl;
      const model = createUploadedImageClipPreviewModel({
        previewBase64: persisted,
        uploadedImage: persisted,
        aspectRatio: "16:9",
      });
      this.manager.setRawClipPreviewModel(serializeClipPreviewModel(model));
      void this.manager.saveProject();
    } catch {
      // Network/CORS failure: initial video snapshot may run instead.
    }
  }

  private flushDebouncedProjectSave(): void {
    void this.manager.saveProject();
  }

  get selectedDestinationIds(): string[] {
    return this.selectedIdsSubject.value;
  }

  getDestinationSettingsUiState(destinationUid: string): DestinationSettingsUiState {
    return this.destinationSettingsUiStateByUid()[destinationUid]
      ?? createIdleDestinationSettingsState();
  }

  setDestinationSettingsUiState(
    destinationUid: string,
    state: DestinationSettingsUiState,
  ): void {
    this.destinationSettingsUiStateByUid.set({
      ...this.destinationSettingsUiStateByUid(),
      [destinationUid]: {
        ...state,
        draftPlatformData: cloneDestinationPlatformData(state.draftPlatformData),
      },
    });
    this.bumpDestinationSettingsUiRevision();
  }

  patchDestinationSettingsUiState(
    destinationUid: string,
    updates: Partial<DestinationSettingsUiState>,
  ): void {
    const current = this.getDestinationSettingsUiState(destinationUid);
    this.setDestinationSettingsUiState(destinationUid, {
      ...current,
      ...updates,
    });
  }

  updateDestinationSettingsDraft(
    destinationUid: string,
    platformData: DestinationPlatformData | null,
  ): void {
    this.patchDestinationSettingsUiState(destinationUid, {
      draftPlatformData: cloneDestinationPlatformData(platformData),
    });
  }

  clearDestinationSettingsUiState(destinationUid: string): void {
    const current = { ...this.destinationSettingsUiStateByUid() };
    delete current[destinationUid];
    this.destinationSettingsUiStateByUid.set(current);
    this.bumpDestinationSettingsUiRevision();
  }

  private bumpDestinationSettingsUiRevision(): void {
    this.destinationSettingsUiRevisionSubject.next(
      this.destinationSettingsUiRevisionSubject.value + 1,
    );
  }

  /**
   * Effective per-destination publish settings for preview cards:
   * draft → saved override → tenant defaults (via resolve* for FB/IG/YT).
   */
  getEffectivePublicationSettingsForPreview(
    destinationUid: string,
    type: DestinationType,
  ): {
    settings: PublicationSettingsSummaryInput | null;
    youtubeCategoryTitle: string | null;
  } {
    const ui = this.getDestinationSettingsUiState(destinationUid);
    const draftOrSaved =
      ui.draftPlatformData ?? this.getDestinationSettingsOverride(destinationUid);
    const defaults = ui.details?.platformData ?? null;

    let youtubeCategoryTitle: string | null = null;
    let settings: PublicationSettingsSummaryInput | null = null;

    switch (type) {
      case "fb":
        settings = resolveFacebookPublicationSettings(
          draftOrSaved,
          (defaults as FacebookDestinationPlatformData | null) ?? null,
        );
        break;
      case "ig":
        settings = resolveInstagramPublicationSettings(
          draftOrSaved,
          (defaults as InstagramDestinationPlatformData | null) ?? null,
        );
        break;
      case "yt": {
        settings = resolveYouTubePublicationSettings(
          draftOrSaved,
          (defaults as YouTubeDestinationPlatformData | null) ?? null,
        );
        const categoryId = settings.category_id?.trim();
        if (categoryId && ui.videoCategories?.items?.length) {
          youtubeCategoryTitle =
            ui.videoCategories.items.find((c) => c.id === categoryId)?.title ?? null;
        }
        break;
      }
      case "tt":
        settings =
          (draftOrSaved as TikTokDestinationPlatformData | null) ??
          (defaults as TikTokDestinationPlatformData | null) ??
          null;
        break;
      default:
        settings = null;
    }

    return { settings, youtubeCategoryTitle };
  }

  setPublishBlockers(source: string, blockers: PublishBlocker[]): void {
    if (!source?.trim()) {
      return;
    }
    const current = { ...this.publishBlockersBySource() };
    if (blockers.length === 0) {
      delete current[source];
    } else {
      current[source] = blockers.map((b) => ({ ...b }));
    }
    this.publishBlockersBySource.set(current);
  }

  clearPublishBlockers(source: string): void {
    this.setPublishBlockers(source, []);
  }

  setPreSubmitError(message: string | null): void {
    this.preSubmitError.set(message?.trim() ? message : null);
  }

  setTikTokPublishContext(context: TikTokPublishContext | null): void {
    const current = { ...this.tikTokPublishContexts() };
    if (context == null) {
      return;
    }
    current[context.destinationUid] = { ...context };
    this.tikTokPublishContexts.set(current);
  }

  clearTikTokPublishContext(destinationUid: string): void {
    const current = { ...this.tikTokPublishContexts() };
    delete current[destinationUid];
    this.tikTokPublishContexts.set(current);
  }

  syncTitleBlockers(): void {
    const selectedIds = this.selectedIdsSignal();
    const destinations = this.destinationsProvider.getDestinations();
    const byId = new Map(destinations.map((d) => [d.id, d]));
    const metadata = this.metadataSubject.value;
    const overrides = this.destinationMetadataOverridesSubject.value;
    const activeSources = new Set<string>();

    for (const dest of destinations) {
      // Clear legacy Facebook-only source keys from earlier builds.
      this.clearPublishBlockers(`facebook-title:${dest.id}`);
    }

    for (const uid of selectedIds) {
      const source = `title:${uid}`;
      activeSources.add(source);
      const dest = byId.get(uid);
      const name = dest?.name ?? UNAVAILABLE_DESTINATION_DISPLAY_NAME;
      const override = overrides[uid];
      const title =
        override?.title !== undefined ? override.title : metadata.title;
      const trimmed = (title ?? "").trim();
      const blockers: PublishBlocker[] = [];

      if (!trimmed) {
        blockers.push({
          code: "title_required",
          message: `Title is required for ${name}.`,
        });
      } else if (trimmed.length > TITLE_MAX_LENGTH) {
        blockers.push({
          code: "title_too_long",
          message: `Title must be ${TITLE_MAX_LENGTH} characters or less for ${name}.`,
        });
      }

      this.setPublishBlockers(source, blockers);
    }

    for (const source of Object.keys(this.publishBlockersBySource())) {
      if (source.startsWith("title:") && !activeSources.has(source)) {
        this.clearPublishBlockers(source);
      }
    }
  }

  syncInstagramCaptionBlockers(): void {
    const selected = new Set(this.selectedIdsSignal());
    const destinations = this.destinationsProvider.getDestinations();
    const metadata = this.metadataSubject.value;
    const overrides = this.destinationMetadataOverridesSubject.value;

    for (const dest of destinations) {
      if (dest.type !== "ig") {
        continue;
      }
      const source = `instagram-caption:${dest.id}`;
      if (!selected.has(dest.id)) {
        this.clearPublishBlockers(source);
        continue;
      }

      const override = overrides[dest.id];
      const title =
        override?.title !== undefined ? override.title : metadata.title;
      const message =
        override?.message !== undefined ? override.message : metadata.message;
      const tags =
        override?.tags !== undefined ? override.tags : metadata.tags;
      const blockers: PublishBlocker[] = [];

      const caption = buildInstagramCaptionPreview(title, message, tags);
      if (caption.length > INSTAGRAM_CAPTION_MAX_LENGTH) {
        blockers.push({
          code: "instagram_caption_too_long",
          message: `Caption must be ${INSTAGRAM_CAPTION_MAX_LENGTH} characters or less for ${dest.name}.`,
        });
      }

      this.setPublishBlockers(source, blockers);
    }
  }

  validateTikTokEditDuration(editDurationMs: number): string | null {
    const contexts = this.selectedTikTokPublishContexts();
    if (contexts.length === 0) {
      return null;
    }

    const editDurationSec = Math.ceil(Math.max(0, editDurationMs) / 1000);
    const failing = contexts.find((ctx) =>
      ctx.maxVideoPostDurationSec > 0 && editDurationSec > ctx.maxVideoPostDurationSec,
    );
    if (!failing) {
      return null;
    }

    return `${failing.destinationName} allows TikTok videos up to ${formatDurationSeconds(failing.maxVideoPostDurationSec)}. Current edit duration is ${formatDurationSeconds(editDurationSec)}.`;
  }


  /** Id of the destination selected in the top-bar dropdown for preview; null = default 16:9. */
  get selectedPreviewDestinationId(): string | null {
    return this.selectedPreviewDestinationIdSubject.value;
  }

  setSelectedPreviewDestinationId(id: string | null): void {
    this.selectedPreviewDestinationIdSubject.next(id ?? null);
    this.persistPublishState();
  }

  /**
   * Aspect ratio for the clipping studio preview. Default 16:9 when no destination is selected;
   * otherwise the selected destination's config aspect ratio (e.g. 9:16 for TikTok/Instagram).
   */
  getPreviewAspectRatio(): AspectRatio {
    const id = this.selectedPreviewDestinationIdSubject.value;
    if (!id) return AspectRatio.Aspect16x9;
    return this.getConfig(id).aspectRatio ?? AspectRatio.Aspect16x9;
  }

  getDestinations() {
    return this.destinationsProvider.getDestinations();
  }

  /**
   * Catalog destinations plus placeholders for selected UIDs missing from the catalog
   * (deleted / disabled). Placeholders disappear once unchecked.
   */
  getDestinationsForUi(): SocialAccount[] {
    const catalog = this.destinationsProvider.getDestinations();
    const known = new Set(catalog.map((d) => d.id));
    const extras = this.selectedIdsSubject.value
      .filter((uid) => uid && !known.has(uid))
      .map((uid) => createUnavailableSocialAccount(uid));
    return extras.length === 0 ? catalog : [...catalog, ...extras];
  }

  isUnavailableDestination(destinationUid: string): boolean {
    if (!destinationUid?.trim()) {
      return false;
    }
    return !this.destinationsProvider
      .getDestinations()
      .some((d) => d.id === destinationUid);
  }

  setSelectedDestinations(ids: string[]): void {
    this.setSelectedIdsInternal([...ids]);
    this.persistPublishState();
  }

  toggleDestination(id: string): void {
    const current = this.selectedIdsSubject.value;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    this.setSelectedIdsInternal(next);
    this.persistPublishState();
  }

  updateDestinationConfig(
    id: string,
    updates: Partial<DestinationConfig>,
  ): void {
    const configs = { ...this.configsSubject.value };
    const current = configs[id] ?? this.getDefaultConfigForDestinationId(id);
    configs[id] = { ...current, ...updates };
    this.configsSubject.next(configs);

    // Keep publish model in sync (aspect ratio lives under destination.publish).
    const tz = this.userService.getDisplayTimezone();
    this.updatePublishModel((m) => {
      const existing = m.destinations[id];
      const dest = existing ?? createDefaultDestinationStateV1({ timeZone: tz });
      const aspectRatio = configs[id].aspectRatio ?? AspectRatio.Aspect16x9;
      return {
        ...m,
        destinations: {
          ...m.destinations,
          [id]: {
            ...dest,
            publish: {
              ...dest.publish,
              aspectRatio,
            },
          },
        },
      };
    });
    this.persistPublishState();
  }

  getConfig(id: string): DestinationConfig {
    return (
      this.configsSubject.value[id] ?? this.getDefaultConfigForDestinationId(id)
    );
  }

  private getDefaultConfigForDestinationId(id: string): DestinationConfig {
    const dest = this.destinationsProvider
      .getDestinations()
      .find((d) => d.id === id);
    const type = dest?.type ?? "yt";
    return this.getDefaultConfigForType(type);
  }

  setExpandedDestination(id: string | null): void {
    this.expandedIdSubject.next(id);
  }

  private persistPublishState(): void {
    if (this.manager.state.projectId == null || this.manager.state.projectId <= 0)
      return;
    this.persistTrigger$.next();
  }

  /** Immediate project save (wizard step boundaries); same path as debounce/publish. */
  saveProjectNow(): Promise<void> {
    return this.manager.saveProject();
  }

  setMetadata(updates: Partial<PublishMetadata>): void {
    this.metadataSubject.next({ ...this.metadataSubject.value, ...updates });
    this.persistPublishState();
  }

  getDestinationMetadataOverride(
    destinationId: string,
  ): DestinationMetadataOverride | null {
    const overrides =
      this.destinationMetadataOverridesSubject.value[destinationId];
    return overrides ?? null;
  }

  setDestinationMetadataOverride(
    destinationId: string,
    field: keyof DestinationMetadataOverride,
    value: string,
  ): void {
    const overrides = { ...this.destinationMetadataOverridesSubject.value };
    const current = overrides[destinationId] ?? {};
    overrides[destinationId] = { ...current, [field]: value };
    this.destinationMetadataOverridesSubject.next(overrides);

    const tz = this.userService.getDisplayTimezone();
    this.updatePublishModel((m) => {
      const existing = m.destinations[destinationId];
      const dest = existing ?? createDefaultDestinationStateV1({ timeZone: tz });
      return {
        ...m,
        destinations: {
          ...m.destinations,
          [destinationId]: {
            ...dest,
            metadata: {
              ...dest.metadata,
              [field]: value,
            },
          },
        },
      };
    });
    this.persistPublishState();
    this.syncTitleBlockers();
    this.syncInstagramCaptionBlockers();
  }

  clearDestinationMetadataOverride(destinationId: string): void {
    const overrides = { ...this.destinationMetadataOverridesSubject.value };
    delete overrides[destinationId];
    this.destinationMetadataOverridesSubject.next(overrides);

    this.updatePublishModel((m) => {
      const existing = m.destinations[destinationId];
      if (!existing) return m;
      const hasOther =
        existing.clipPreview != null ||
        existing.publish.destinationSettings != null;
      if (!hasOther) {
        const { [destinationId]: _removed, ...rest } = m.destinations;
        return { ...m, destinations: rest };
      }
      return {
        ...m,
        destinations: {
          ...m.destinations,
          [destinationId]: {
            ...existing,
            metadata: {},
          },
        },
      };
    });
    this.persistPublishState();
    this.syncTitleBlockers();
    this.syncInstagramCaptionBlockers();
  }

  updateMetadataField<K extends keyof PublishMetadata>(
    key: K,
    value: PublishMetadata[K],
  ): void {
    this.metadataSubject.next({
      ...this.metadataSubject.value,
      [key]: value,
    });
    this.persistPublishState();
    this.syncTitleBlockers();
    this.syncInstagramCaptionBlockers();
  }

  setDestinationSettingsOverride(
    destinationUid: string,
    platformData: DestinationPlatformData | null,
  ): void {
    const tz = this.userService.getDisplayTimezone();
    this.updatePublishModel((m) => {
      const existing = m.destinations[destinationUid];
      const dest = existing ?? createDefaultDestinationStateV1({ timeZone: tz });
      return {
        ...m,
        destinations: {
          ...m.destinations,
          [destinationUid]: {
            ...dest,
            publish: {
              ...dest.publish,
              destinationSettings: platformData,
            },
          },
        },
      };
    });
    this.persistPublishState();
  }

  private setSelectedIdsInternal(ids: string[]): void {
    const next = [...ids];
    this.selectedIdsSubject.next(next);
    this.selectedIdsSignal.set(next);
    this.setPreSubmitError(null);
    this.syncTitleBlockers();
    this.syncInstagramCaptionBlockers();
  }

  /** Request clip preview snapshot (same as clicking Snapshot button in timeline). Root component handles the actual capture. */
  requestClipPreviewSnapshot(
    aspectRatio?: ClipPreviewAspectRatio,
    destinationUid?: string | null,
  ): void {
    this.manager.requestClipPreviewSnapshot(aspectRatio, destinationUid);
  }

  getCurrentClipPreviewModel() {
    return this.manager.getClipPreviewModel();
  }

  getClipPreviewEditorSourceImage(): Promise<string | null> {
    return this.manager.getClipPreviewEditorSourceImage();
  }

  /** @deprecated Use applyClipPreviewFromEditor(destinationUid, args) from thumbnail tab. */
  setClipPreviewFromEditorResult(args: {
    previewBase64: string;
    aspectRatio: ClipPreviewAspectRatio;
    editor: ClipPreviewEditorState;
    uploadedImage?: string | null;
  }): void {
    void this.applyClipPreviewFromEditor(null, args);
  }

  /** Sets clip preview from a user file (legacy compatibility). */
  async setClipPreviewFromUpload(dataUrl: string | null): Promise<void> {
    if (!dataUrl) {
      this.manager.setRawClipPreviewModel(null);
      return;
    }
    const persisted = (await encodeClipPreviewPersistedDataUrl(dataUrl)) ?? dataUrl;
    this.manager.setClipPreviewFromEditorResult({
      previewBase64: persisted,
      uploadedImage: persisted,
      aspectRatio: "16:9",
      editor: {
        zoom: 100,
        offsetX: 0,
        offsetY: 0,
      },
    });
  }

  private async encodePersistedClipPreviewApplyArgs(args: {
    previewBase64: string;
    aspectRatio: ClipPreviewAspectRatio;
    editor: ClipPreviewEditorState;
    uploadedImage?: string | null;
  }): Promise<{
    previewBase64: string;
    aspectRatio: ClipPreviewAspectRatio;
    editor: ClipPreviewEditorState;
    uploadedImage?: string | null;
  }> {
    const previewBase64 =
      (await encodeClipPreviewPersistedDataUrl(args.previewBase64)) ?? args.previewBase64;
    let uploadedImage = args.uploadedImage;
    if (uploadedImage) {
      uploadedImage =
        (await encodeClipPreviewPersistedDataUrl(uploadedImage)) ?? uploadedImage;
    }
    return {
      ...args,
      previewBase64,
      uploadedImage,
    };
  }

  async triggerAiComplete(
    allowedRatings?: string[],
    destinationId?: string,
    destinationForApi?: string,
  ): Promise<void> {
    const videoSlug = this.manager.state.media?.videoSlug?.trim();
    if (!videoSlug) {
      return;
    }
    this.isAICompletingSubject.next(true);
    this.isAICompletingSignal.set(true);
    try {
      const options: { allowedRatings?: string[]; destination?: string } =
        allowedRatings?.length ? { allowedRatings } : {};
      if (destinationForApi?.trim()) {
        options.destination = destinationForApi.trim();
      }
      const res = await this.generateMetadataRepository.generateMetadata(
        videoSlug,
        Object.keys(options).length ? options : undefined,
      );
      if (destinationId) {
        const overrides = { ...this.destinationMetadataOverridesSubject.value };
        overrides[destinationId] = {
          title: res.title,
          message: res.message,
          tags: res.tags,
        };
        this.destinationMetadataOverridesSubject.next(overrides);

        const tz = this.userService.getDisplayTimezone();
        this.updatePublishModel((m) => {
          const existing = m.destinations[destinationId];
          const dest =
            existing ?? createDefaultDestinationStateV1({ timeZone: tz });
          return {
            ...m,
            destinations: {
              ...m.destinations,
              [destinationId]: {
                ...dest,
                metadata: {
                  title: res.title,
                  message: res.message,
                  tags: res.tags,
                },
              },
            },
          };
        });
      } else {
        this.metadataSubject.next({
          ...this.metadataSubject.value,
          title: res.title,
          tags: res.tags,
          message: res.message,
        });
      }
      this.syncTitleBlockers();
      this.syncInstagramCaptionBlockers();
      this.persistPublishState();
    } finally {
      this.isAICompletingSubject.next(false);
      this.isAICompletingSignal.set(false);
    }
  }
}


